import * as $ from 'jquery';
import * as conf from 'CMS/conf';
import * as url from 'CMS/core/url';
import * as i18n from 'CMS/core/i18n';
import * as kendo from 'kendo/kendo.core';
import Event, { IEvent } from 'CMS/Event/Event';
import Grid from 'AWFGrid/Grids/Grid';
import { IGridOptions, IFilterGridOptions } from 'AWFGrid/Grids/IGridOptions';
import FiltersList from 'AWFGrid/Filters/FiltersList';
import { alert } from 'CMS/UI/Popup';

// Imports without variable
import 'kendo/kendo.data';
import 'kendo/kendo.grid';
import 'kendo/kendo.pager';

export default class FilterGrid {
    protected options: IFilterGridOptions;
    protected $root: JQuery;
    protected grid: Grid;
    private filtersList: FiltersList;
    private selectable: boolean;
    private detailsDisabled: boolean;

    public selected: IEvent<any> = new Event<any>();

    public constructor(detailsDisabled: boolean = false, selectable: boolean = false) {
        this.detailsDisabled = detailsDisabled;
        this.selectable = selectable;
    }

    public async initialize($root: JQuery, options: IFilterGridOptions): Promise<FilterGrid> {
        this.$root = $root;

        /* Set default options */
        this.options = $.extend({}, { autoLoad: true }, options);

        /* Hide if isVisible is false */
        if (this.options.isVisible === false) {
            this.$root.hide();
        }

        this.detailsDisabled = this.detailsDisabled || options.detailsDisabled;

        /* Load template */
        const html = await import('text!AWFGrid/Grids/FilterGrid.html');

        /* Hide the required elements based on options & setup jquery handlers */
        this.$root.append(html);
        if (this.options.filterTagsVisible === false) {
            this.$root.find('.tags-cloud-container').addClass('hidden');
        }
        if (this.options.filterListVisible === false || this.options.filterType == null) {
            /* Hide filters column */
            this.$root.find('.col-filter-list').addClass('hidden');
            this.$root.find('.col-grid').removeClass('col-md-10').addClass('col-md-12');
        } else {
            /* Show filter button */
            this.$root.find('.filters-container-button').removeClass('hidden');

            if (this.options.filterListVisible === 'button') {
                /* Hide filters column */
                this.$root.find('.col-filter-list').removeClass('col-md-2');
                this.$root.find('.col-filter-list').parent().addClass('filters-button-visible');
                this.$root.find('.col-grid').removeClass('col-md-10').addClass('col-md-12');

                /* Show close button in filter column */
                this.$root.find('.col-filter-list .btn-close').removeClass('hidden');
            } else {
                /* Only show filter button on mobile screens */
                this.$root.find('.filters-container-button').addClass('visible-xs visible-sm');
            }



            this.$root.find('.btn-close').on('click', (e) => {
                e.preventDefault();
                this.$root.find('.col-filter-list').addClass('collapsed');
                this.$root.find('.col-filter-list-overlay').addClass('hidden');
            });
            this.$root.find('.col-filter-list-overlay').on('click', (e) => {
                this.$root.find('.col-filter-list').addClass('collapsed');
                this.$root.find('.col-filter-list-overlay').addClass('hidden');
            });

            this.$root.find('.btn-filter').on('click', (e) => {
                e.preventDefault();
                this.$root.find('.col-filter-list').removeClass('collapsed');
                this.$root.find('.col-filter-list-overlay').removeClass('hidden');
            });
        }


        /* Load the grid & translations*/
        await this.createGrid();
        i18n.bind(this.$root);

        return this;
    }

    /**
     * Load the data if autoLoad is false
     * */
    public load(): Promise<void> {
        return this.grid.load();
    }

    /**
     * Create the grid
     */
    protected async createGrid(): Promise<Grid> {
        this.$root.find('.grid-container').empty();

        /* Create grid */
        this.grid = new Grid(this.detailsDisabled, this.selectable);

        /* Add selected event */
        this.grid.selected.on((data) => {
            (<Event<any>>this.selected).trigger(data);
        });

        /* Only show add button if allowed */
        const $btnAdd = this.$root.find('.btn-add');
        if (this.options.canAddNew != null && this.options.canAddNew) {
            $btnAdd.toggleClass('hidden', false);
            /* Add button click handler */
            $btnAdd.on('click', (e) => {
                e.preventDefault();
                (<Event<any>>this.grid.addNew).trigger();
            });
        }


        /* Clone options */
        const gridOptions = <IGridOptions>$.extend(true, { reload: () => { this.reload(); }, visible: (visible: boolean) => { this.visible(visible); } }, this.options);

        /* Prevent the underlying grid from loading automatically unless autoLoad === 'immediate' */
        if (this.options.autoLoad === 'immediate') {
            gridOptions.autoLoad = true;
        } else {
            /* NOTE: sets autoLoad = false even if the FilterGrid has autoLoad = true 
             * The underlying grid can only be loaded correctly after the filters are loaded! */
            gridOptions.autoLoad = false;
        }

        /* Initialize filters & grid */
        const filtersPromise = this.createFilters(this.grid);
        const gridPromise = this.grid.initialize(this.$root.find('.grid-container')[0], gridOptions);

        /* Load grid & filters */
        const values = await Promise.all([gridPromise, filtersPromise]);

        this.filtersList = values[1];

        /* Load data in case of auto load */
        if (this.options.autoLoad === true) {
            this.grid.load(); /* dont await (backwards compatible) */
        }

        return values[0];
    }

    /**
     * Create the filters
     */
    protected async createFilters(grid: Grid): Promise<FiltersList> {
        this.$root.find('.filters-container').empty();
        const filtersList = new FiltersList();

        /* Initialize filter list */
        const filterListPromise = filtersList.initialize(this.$root.find('.filters-container')[0], { type: this.options.filterType, config: 'grids' });

        /* Initialize filter list with data once the grid is loaded */
        grid.dataBound.on((data: any[]) => {
            filtersList.dataBound(data);
        });

        /* Initalize tags cloud */
        var $tagsCloud = $('<div />');
        $tagsCloud.appendTo(this.$root.find('.tags-cloud-container'));
        filtersList.initializeTagsCloud($tagsCloud[0]);
        /* Listen to filters changed event and apply to grid */
        filtersList.filtersChanged.on(filters => {
            /* If max filters (=20) selected, show error message and don't call the API to prevent error */
            let amountFilters = 0;
            const filterGroups = filters as kendo.data.DataSourceFilters;
            for (let filterGroup of filterGroups.filters) {
                const fg = filterGroup as kendo.data.DataSourceFilters;
                if ("operator" in fg) { // contains
                    amountFilters++;
                }
                if (fg.filters && fg.filters.length) { // eq
                    amountFilters += fg.filters.length;
                }
            }
            if (amountFilters > 20) {
                alert(i18n.getMessage('filterList.maxFiltersReached'));
            }
            this.grid.filter(filters);
        });
        filtersList.searchChanged.on(search => {
            this.grid.search(search);
        });



        /* Wait until filter list has been initialized */
        const filterList = await filterListPromise;
        this.$root.find('.filters-container-title').removeClass('hidden');

        /* Filter Grid */
        if (filtersList.hasFilters()) {
            this.grid.filter(filtersList.getFilters());
        }

        return filterList;
    }

    public visible(visible: boolean): void {
        visible ? this.$root.show() : this.$root.hide();
    }

    private reload(): void {
        this.filtersList.reload();
        this.grid.reload(this.filtersList.getFilters());

    }

    public destroy(): void { }
}
